import { Check, Edit2, X, Equal } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import TooltipForce from '../../ui/tooltip-force'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogTrigger } from '../../ui/alert-dialog'
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import { useDebounce } from '@uidotdev/usehooks'
import useAuth from '../../../hooks/useAuth'
import axios from '../../../lib/axios'

import { Button } from '../../ui/button'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { GenericCoin, GenericNote } from '../../elements/'

type Props = {
    keepsafe?: AnnouncementKeepsafeForeignCurrency;
    onSave: (object:{barcode:string, coupures:{qty:number, coupure:number}[]}) => void;
    deviesCurrency: DeviesCurrency
}

const EditKeepsafeForeignCurrency : React.FC<Props> = ({keepsafe, onSave, deviesCurrency}) => {
    const {t} = useTranslation()
    const {auth} = useAuth()
    const [barcode, setbarcode] = useState<string>('');
    const [barcode_error, setbarcode_error] = useState<'correct' | StateType>(null);
    const debouncedBarcode = useDebounce(barcode, 300)

    const [coupure_input, setcoupure_input] = useState<{qty:number, coupure:number}[]>();

    useEffect(()=>{
        if(keepsafe){
            setbarcode(keepsafe.keepsafe);
            setcoupure_input(keepsafe.detail[0].coupures)
        }
    }, [keepsafe])

    useEffect(()=>{
        if(debouncedBarcode !== '' && debouncedBarcode.substring(0,2) === 'BE') {
            const checkBarcode = async () => {
                try {
                    setbarcode_error('loading')
                    const { data } = await axios.post(`barcode/check`, {
                        token: auth.data?.token,
                        barcode: debouncedBarcode
                    })
                    if(data.errorcode === 0){
                        if(data.object.barcode.valid === true && data.object.barcode.remark === ''){
                            setbarcode_error('correct')
                        } else {
                            setbarcode_error('error')
                        }
                    }
                    else {
                        setbarcode_error('error')
                    }
                } catch (error) {
                    setbarcode_error('error')
                    console.log(error)
                }
            }
            checkBarcode()
        }
        else {
            setbarcode_error(null)
        }
    }, [debouncedBarcode])

    const onChangeInput = (coupure:number, qty:number) => {
        qty = Math.round(qty)
        if(coupure_input) {
            let tmp = [...coupure_input]
            const existingIndex = coupure_input.findIndex(item => item.coupure === coupure);
            
            if(existingIndex !== -1){
                tmp[existingIndex] = {qty:qty, coupure:coupure}
            }
            else {
                tmp.push({qty:qty, coupure:coupure})
            }
            console.log(tmp)
            setcoupure_input(tmp);
        }
        else {
            setcoupure_input([{coupure:coupure, qty:qty}])
        }
    };

    const calculateTotal = (cn:'c' | 'n') => {
        let total = 0;

        if(coupure_input) {
            let tmp = [...coupure_input]
            if(cn === 'c') { tmp = tmp.filter(i =>i.coupure <= 2); }
            else { tmp = tmp.filter(i => i.coupure > 2) }
            

            tmp.forEach(item => {
                if(!isNaN(item.qty) && !isNaN(item.coupure)){
                    total += item.qty * item.coupure
                }
            });
        }

        return total;
    }

    const calculateSubtotal = (coupure:number) => {
        let total = 0;
        if(coupure_input) {
            const item = coupure_input.find(i=>i.coupure===coupure)
            if(item) {
                total = item.coupure * item.qty
            }
        }
        return Math.round(total * 100)/100 || 0
    }

    const saveKeepsafe = () => {
        if(coupure_input){
            onSave({
                barcode: barcode,
                coupures: coupure_input
            })
        } else {
            toast.error(t("order_and_pickup.o_announce_pickup.o_manual.toast_no_found_p3"))
        }
    }
    
    const CoinsInput = () => {
        return (
            <div className='flex flex-col gap-2'>
                {deviesCurrency.devies.filter(i=>i.cn.toLowerCase() === 'c').map((devies) => (
                    <div className='grid grid-cols-2' key={`coin-devies-${devies.value}`}>
                        <div className='flex items-center gap-2'>
                            {devies.cn.toLowerCase() === 'n'
                                ? <GenericNote value={devies.value} currency={deviesCurrency.deviescurrencyname || 'EUR'} className='w-16 h-8'/>
                                : <GenericCoin value={devies.value} currency={deviesCurrency.deviescurrencyname || 'EUR'} className='w-8 h-8'/>}
                            <p className='font-medium'>{devies.value} {deviesCurrency.deviescurrencyname}</p>
                        </div>
                        <div className='grid grid-cols-3'>
                            <Input onBlur={({currentTarget})=>{ onChangeInput(devies.value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === devies.value)?.qty || ''} className='w-20' type='number' min={0}/>
                            <div className='flex items-center justify-center'><Equal className='w-5' strokeWidth={1}/></div>
                            <div className='flex items-center justify-end'><p className='font-bold text-right'>{calculateSubtotal(devies.value)} {deviesCurrency.deviescurrencyname}</p></div>
                        </div>
                    </div>
                ))}

                <div className='flex justify-end py-2 text-sm border-t'>
                    <p>{t("order_and_pickup.o_announce_pickup.o_manual.total")}: <span className='font-medium'>€ {calculateTotal('c')}</span></p> 
                </div>
            </div>
        )
    }

    const NotesInput = () => {
        return (
            <div className='flex flex-col gap-2'>
                {deviesCurrency.devies.filter(i=>i.cn.toLowerCase() === 'n').map((devies) => (
                    <div className='grid grid-cols-2' key={`coin-devies-${devies.value}`}>
                        <div className='flex items-center gap-2'>
                            {devies.cn.toLowerCase() === 'n'
                                ? <GenericNote value={devies.value} currency={deviesCurrency.deviescurrencyname || 'EUR'} className='w-16 h-8'/>
                                : <GenericCoin value={devies.value} currency={deviesCurrency.deviescurrencyname || 'EUR'} className='w-8 h-8'/>}
                            <p className='font-medium'>{devies.value} {deviesCurrency.deviescurrencyname}</p>
                        </div>
                        <div className='grid grid-cols-3'>
                            <Input onBlur={({currentTarget})=>{ onChangeInput(devies.value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === devies.value)?.qty || ''} className='w-20' type='number' min={0}/>
                            <div className='flex items-center justify-center'><Equal className='w-5' strokeWidth={1}/></div>
                            <div className='flex items-center justify-end'><p className='font-bold text-right'>{calculateSubtotal(devies.value)} {deviesCurrency.deviescurrencyname}</p></div>
                        </div>
                    </div>
                ))}

                <div className='flex justify-end py-2 text-sm border-t'>
                    <p>{t("components.o_announcement.o_announcement.total")}: <span className='font-medium'>€ {calculateTotal('n')}</span></p> 
                </div>
            </div>
        )
    }

  return (
    <AlertDialog>
        
        <AlertDialogTrigger asChild>
            <TooltipForce content={<p className='whitespace-nowrap'>{t("order_and_pickup.o_announce_pickup.o_manual.tooltip_edit")}</p>}>
            <button className='flex items-center justify-center w-8 h-8 duration-200 opacity-50 hover:opacity-90 group'><Edit2 className='w-5 group-hover:text-orange-500'/></button>
            </TooltipForce>
        </AlertDialogTrigger>
        <AlertDialogContent className='max-h-[95vh] overflow-y-auto'>
            <div className='flex items-center justify-between'>
                <h2 className='text-xl font-medium text-black/80'>{t("order_and_pickup.o_announce_pickup.o_manual.edit_title")}</h2>
                <AlertDialogCancel asChild><button className='w-6 h-6 !p-0 bg-transparent border-none opacity-50 hover:bg-transparent hover:opacity-100'><X /></button></AlertDialogCancel>
            </div>

            <div>
                <Label htmlFor='barcode'>{t("order_and_pickup.o_announce_pickup.o_manual.barcode")}<span className='ml-1 text-primary'>*</span></Label>
                <Input endEnhancer={
                <>
                    {barcode_error === 'correct' && <div className='flex items-center justify-center w-5 h-5 bg-green-400 rounded-full'><Check className='w-3 text-white'/></div>}
                    {barcode_error === 'error' && <div className='flex items-center justify-center w-5 h-5 bg-red-400 rounded-full'><X className='w-3 text-white'/></div>}
                </>} onChange={({currentTarget})=>setbarcode(currentTarget.value)} value={barcode} id='barcode'/>
                {barcode_error === 'error' && <p className='text-sm text-red-600'>{t("order_and_pickup.o_announce_pickup.o_manual.barcode_error")}</p>}
            </div>

            {deviesCurrency.devies.find(d => d.value === keepsafe?.detail[0].coupures[0].coupure)?.cn === 'c'
            ? 
            <CoinsInput />
            :
            <NotesInput />
            }

            <div>
                <AlertDialogAction disabled={barcode === '' || !(calculateTotal('c') > 0 || calculateTotal('n') > 0)} onClick={()=>{saveKeepsafe()}}>{t("order_and_pickup.o_announce_pickup.o_manual.edit_save")}</AlertDialogAction>
                <Button onClick={()=>{setcoupure_input(undefined)}} variant='ghost'>{t("order_and_pickup.o_announce_pickup.o_manual.edit_clear")}</Button>
            </div>
        </AlertDialogContent>
        
    </AlertDialog>
  )
}

export default EditKeepsafeForeignCurrency